import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../../style/attendanceContent.css";
import { PageHeader } from "../../components/admin/AdminUI";

const TEAL = "#0d9488";

const swalError = (text) =>
  Swal.fire({ text, icon: "error", background: "white", color: "black", confirmButtonColor: TEAL });

// Warna badge status kelas. NOT_TAKEN sengaja abu-abu, bukan merah: kelas yang
// belum diambil BUKAN kegagalan — di spreadsheet lama keadaan ini tampil
// sebagai #N/A, dan menampilkannya merah akan membuat semua student baru
// terlihat seperti gagal 9 kelas.
const STATUS_STYLE = {
  PASSED: { bg: "#e6f4ee", fg: "#1d7a52" },
  FAIL: { bg: "#fdeaea", fg: "#b93030" },
  NOT_TAKEN: { bg: "#f0f0f0", fg: "#888" },
};

const GRADE_COLOR = { A: "#1d7a52", B: "#2e9e6b", C: "#b8860b", D: "#d97706", F: "#b93030" };

const Badge = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.NOT_TAKEN;
  const label = status === "NOT_TAKEN" ? "—" : status;
  return (
    <span
      style={{
        backgroundColor: s.bg,
        color: s.fg,
        borderRadius: "4px",
        padding: "2px 8px",
        fontSize: "0.78em",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

const Grade = ({ letter }) =>
  letter ? (
    <span style={{ fontWeight: 700, color: GRADE_COLOR[letter] || "#555" }}>{letter}</span>
  ) : (
    <span style={{ color: "#bbb" }}>—</span>
  );

const num = (v, dash = "—") => (v === null || v === undefined ? dash : v);

const card = {
  backgroundColor: "white",
  border: "1px solid var(--adm-border)",
  borderRadius: "12px",
  boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
  padding: "20px 22px",
  marginBottom: "20px",
};

const th = {
  textAlign: "left",
  padding: "10px 12px",
  fontSize: "11.5px",
  color: "#6b7280",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  backgroundColor: "#fafafa",
  borderBottom: "1px solid var(--adm-border)",
  whiteSpace: "nowrap",
};

const td = { padding: "10px 12px", fontSize: "13.5px", borderBottom: "1px solid var(--adm-border-soft)", verticalAlign: "middle" };

const input = { height: "40px", padding: "0 12px", border: "1px solid var(--adm-border)", borderRadius: "8px", fontSize: "14px", backgroundColor: "#fff" };

const btn = (active) => ({
  height: "36px",
  padding: "0 16px",
  border: `1px solid ${active ? TEAL : "var(--adm-border)"}`,
  borderRadius: "999px",
  fontSize: "13.5px",
  fontWeight: 600,
  cursor: "pointer",
  backgroundColor: active ? TEAL : "#fff",
  color: active ? "white" : "var(--adm-text-2)",
  whiteSpace: "nowrap",
});

const smallBtn = {
  height: "32px",
  padding: "0 12px",
  border: "1px solid var(--adm-border)",
  borderRadius: "8px",
  fontSize: "12.5px",
  fontWeight: 600,
  cursor: "pointer",
  backgroundColor: "white",
  color: "var(--adm-text)",
  whiteSpace: "nowrap",
};

// Tanggal LOKAL hari ini (YYYY-MM-DD). toISOString() memakai UTC, jadi di
// WIB sebelum jam 07.00 ia masih menunjuk tanggal kemarin.
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const DOC_LABEL = {
  monthly: "Attendance Summary",
  semester1: "Attendance & Grading Semester 1",
  semester2: "Attendance & Grading Semester 2",
  skl: "Transkrip Nilai (SKL)",
};

// Pilihan di UI -> parameter API. "semester1"/"semester2" adalah satu tipe
// dokumen yang sama di backend, dibedakan oleh `semester`.
const docParams = (docKey, sklDate) =>
  docKey === "skl"
    ? { type: "skl", documentDate: sklDate }
    : docKey === "monthly"
      ? { type: "monthly" }
      : { type: "semester", semester: docKey === "semester1" ? 1 : 2 };

const escapeHtml = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]);

const listHtml = (title, rows, render) =>
  rows.length === 0
    ? ""
    : `<p style="margin:12px 0 4px;font-weight:600;font-size:0.9em;text-align:left">${title} (${rows.length})</p>` +
      `<div style="max-height:160px;overflow:auto;text-align:left;font-size:0.82em;border:1px solid #eee;border-radius:6px;padding:6px 10px">` +
      rows.map((r) => `<div style="padding:2px 0">${render(r)}</div>`).join("") +
      `</div>`;

const ReportPage = ({ setLocationAdmin }) => {
  const navigate = useNavigate();
  const address = useLocation();
  const [errorMe, setErrorMe] = useState(false);
  const [tab, setTab] = useState("class");

  // ── Tab: Per Kelas ────────────────────────────────────────────────────────
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState("");
  const [classSearch, setClassSearch] = useState("");
  const [appliedClassSearch, setAppliedClassSearch] = useState("");
  const [classPage, setClassPage] = useState(1);
  const [classReport, setClassReport] = useState(null);
  const [loadingClass, setLoadingClass] = useState(false);
  // Nilai tugas yang SEDANG diketik, per email. Dipisahkan dari classReport
  // supaya angka di layar tidak melompat balik ke nilai server di tiap
  // ketikan; baru disatukan setelah tersimpan.
  const [scoreDraft, setScoreDraft] = useState({});
  const [savingScore, setSavingScore] = useState({});

  // ── Tab: Per Student ──────────────────────────────────────────────────────
  const [studentQuery, setStudentQuery] = useState("");
  const [appliedStudentQuery, setAppliedStudentQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [studentReport, setStudentReport] = useState(null);
  const [loadingStudent, setLoadingStudent] = useState(false);
  const printRef = useRef(null);

  // ── Tab: Graduation ───────────────────────────────────────────────────────
  const [gradSearch, setGradSearch] = useState("");
  const [appliedGradSearch, setAppliedGradSearch] = useState("");
  const [gradStatus, setGradStatus] = useState("");
  const [gradBatch, setGradBatch] = useState("");
  const [gradPage, setGradPage] = useState(1);
  const [gradList, setGradList] = useState(null);
  // Jumlah eligible untuk SELURUH student yang cocok filter — bukan cuma
  // halaman yang terbuka. Endpoint terpisah karena "eligible" hasil hitungan,
  // bukan kolom DB, jadi angkanya hanya bisa didapat dengan menghitung semua.
  const [gradStats, setGradStats] = useState(null);
  const [loadingGrad, setLoadingGrad] = useState(false);

  // ── Kirim dokumen PDF ke student ──────────────────────────────────────────
  // Pilihan disimpan sebagai daftar email, bukan indeks baris, supaya tetap
  // benar saat pindah halaman tabel.
  const [classPicked, setClassPicked] = useState([]);
  const [gradPicked, setGradPicked] = useState([]);
  const [gradDoc, setGradDoc] = useState("semester1");
  const [sklDate, setSklDate] = useState(todayIso);
  const [downloading, setDownloading] = useState("");

  // ── Tab: Riwayat Kirim ────────────────────────────────────────────────────
  const [histSearch, setHistSearch] = useState("");
  const [appliedHistSearch, setAppliedHistSearch] = useState("");
  const [histType, setHistType] = useState("");
  const [histStatus, setHistStatus] = useState("");
  const [histPage, setHistPage] = useState(1);
  const [history, setHistory] = useState(null);
  const [loadingHist, setLoadingHist] = useState(false);
  // Dinaikkan setiap pengiriman selesai, supaya riwayat dimuat ulang.
  const [histTick, setHistTick] = useState(0);

  const getMe = async () => {
    try {
      await axios.get("/meAdmin");
    } catch (error) {
      setErrorMe(true);
    }
  };

  useEffect(() => {
    setLocationAdmin(address);
    getMe();
    (async () => {
      try {
        const res = await axios.get("/report/classes");
        setOptions(res.data);
        if (res.data.length > 0) setSelected(`${res.data[0].classCode}|${res.data[0].batch}`);
      } catch (error) {}
    })();
  }, []);

  useEffect(() => {
    if (errorMe) {
      Swal.fire({
        title: "Session Expired",
        text: "Please login to your account !",
        icon: "warning",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Ok",
      });
      navigate("/loginAdmin");
    }
  }, [errorMe, navigate]);

  // Debounce 350ms + reset page di tick yang SAMA — kalau page direset di efek
  // terpisah, mengetik saat berada di halaman 3 menembak dua request (page 3
  // yang kosong lalu page 1). Pola ini menyalin AttendanceGridPage.
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedClassSearch(classSearch);
      setClassPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [classSearch]);

  useEffect(() => {
    const t = setTimeout(() => setAppliedStudentQuery(studentQuery), 400);
    return () => clearTimeout(t);
  }, [studentQuery]);

  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedGradSearch(gradSearch);
      setGradPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [gradSearch]);

  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedHistSearch(histSearch);
      setHistPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [histSearch]);

  // Riwayat pengiriman dokumen
  useEffect(() => {
    if (tab !== "history") return;
    let cancelled = false;
    setLoadingHist(true);
    axios
      .get("/report/document/history", {
        params: {
          page: histPage,
          ...(appliedHistSearch ? { search: appliedHistSearch } : {}),
          ...(histType ? { type: histType } : {}),
          ...(histStatus ? { status: histStatus } : {}),
        },
      })
      .then((res) => {
        if (!cancelled) setHistory(res.data);
      })
      .catch(() => {
        if (!cancelled) setHistory(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingHist(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, histPage, appliedHistSearch, histType, histStatus, histTick]);

  // Pilihan student milik satu kelas+batch — ganti kelas, pilihan dikosongkan.
  useEffect(() => {
    setClassPicked([]);
  }, [selected]);

  // Fetch raport per kelas
  useEffect(() => {
    if (!selected) return;
    const [classCode, batch] = selected.split("|");
    let cancelled = false;
    setLoadingClass(true);
    axios
      .get(`/report/${classCode}/${batch}`, {
        params: { page: classPage, ...(appliedClassSearch ? { search: appliedClassSearch } : {}) },
      })
      .then((res) => {
        if (!cancelled) setClassReport(res.data);
      })
      .catch(() => {
        if (!cancelled) setClassReport(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingClass(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selected, classPage, appliedClassSearch]);

  // Kandidat student untuk tab Per Student
  useEffect(() => {
    if (!appliedStudentQuery.trim()) {
      setCandidates([]);
      return;
    }
    let cancelled = false;
    axios
      .get("/report/graduation", { params: { search: appliedStudentQuery, pageSize: 8 } })
      .then((res) => {
        if (!cancelled) setCandidates(res.data.students);
      })
      .catch(() => {
        if (!cancelled) setCandidates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [appliedStudentQuery]);

  // Statistik eligible. Sengaja TIDAK bergantung pada gradStatus: angka total
  // inilah yang dipakai untuk memahami filter status, jadi kalau ia sendiri
  // ikut difilter status, hasilnya jadi sirkular (selalu 100%).
  useEffect(() => {
    if (tab !== "graduation") return;
    let cancelled = false;
    setGradStats(null);
    axios
      .get("/report/graduation/stats", {
        params: {
          ...(appliedGradSearch ? { search: appliedGradSearch } : {}),
          ...(gradBatch ? { batchJoined: gradBatch } : {}),
        },
      })
      .then((res) => {
        if (!cancelled) setGradStats(res.data);
      })
      .catch(() => {
        if (!cancelled) setGradStats(null);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, appliedGradSearch, gradBatch]);

  // Daftar graduation
  useEffect(() => {
    if (tab !== "graduation") return;
    let cancelled = false;
    setLoadingGrad(true);
    axios
      .get("/report/graduation", {
        params: {
          page: gradPage,
          ...(appliedGradSearch ? { search: appliedGradSearch } : {}),
          ...(gradStatus ? { status: gradStatus } : {}),
          ...(gradBatch ? { batchJoined: gradBatch } : {}),
        },
      })
      .then((res) => {
        if (!cancelled) setGradList(res.data);
      })
      .catch(() => {
        if (!cancelled) setGradList(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingGrad(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, gradPage, appliedGradSearch, gradStatus, gradBatch]);

  // Simpan satu sel nilai tugas. Kosong = hapus isian manual, sehingga raport
  // kembali memakai rata-rata Google Classroom (kalau ada).
  const saveScore = async (row) => {
    if (!classReport) return;
    const raw = scoreDraft[row.email];
    if (raw === undefined) return; // tidak disentuh

    const trimmed = String(raw).trim();
    if (trimmed !== "" && (isNaN(Number(trimmed)) || Number(trimmed) < 0 || Number(trimmed) > 100)) {
      swalError("Nilai tugas harus angka 0-100, atau kosongkan untuk memakai nilai Google Classroom.");
      return;
    }

    setSavingScore((p) => ({ ...p, [row.email]: true }));
    try {
      const res = await axios.put("/report/assignment-score", {
        studentEmail: row.email,
        classCode: classReport.classCode,
        batch: classReport.batch,
        score: trimmed === "" ? null : Number(trimmed),
      });
      // Respons PUT sudah membawa baris yang dihitung ulang (total/grade/
      // status), jadi cukup ditimpa di state — tidak perlu memuat ulang
      // seluruh tabel, yang akan melompatkan posisi scroll.
      if (res.data.result) {
        setClassReport((prev) =>
          prev
            ? {
                ...prev,
                students: prev.students.map((st) =>
                  st.email === row.email ? { ...st, ...res.data.result } : st,
                ),
              }
            : prev,
        );
      } else {
        // Penghapusan tidak mengembalikan result — muat ulang halaman tabel.
        const [cc, b] = selected.split("|");
        const fresh = await axios.get(`/report/${cc}/${b}`, {
          params: { page: classPage, ...(appliedClassSearch ? { search: appliedClassSearch } : {}) },
        });
        setClassReport(fresh.data);
      }
      setScoreDraft((p) => {
        const next = { ...p };
        delete next[row.email];
        return next;
      });
    } catch (error) {
      swalError(error.response?.data?.msg || "Gagal menyimpan nilai tugas.");
    } finally {
      setSavingScore((p) => {
        const next = { ...p };
        delete next[row.email];
        return next;
      });
    }
  };

  const openStudent = async (email) => {
    setTab("student");
    setLoadingStudent(true);
    setStudentReport(null);
    try {
      const res = await axios.get(`/report/student/${encodeURIComponent(email)}`);
      setStudentReport(res.data);
    } catch (error) {
      swalError(error.response?.data?.msg || "Gagal memuat raport student.");
    } finally {
      setLoadingStudent(false);
    }
  };

  // Unduh satu PDF untuk diperiksa sebelum dikirim. Diunduh lewat axios
  // (bukan link langsung) supaya pesan kesalahan dari server bisa ditampilkan.
  const downloadDocument = async (params, key) => {
    setDownloading(key);
    try {
      const res = await axios.get("/report/document/preview", { params, responseType: "blob" });
      const match = /filename\*=UTF-8''([^;]+)/.exec(res.headers["content-disposition"] || "");
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? decodeURIComponent(match[1]) : "dokumen.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      // responseType blob membuat pesan error ikut datang sebagai Blob.
      let msg = "Gagal membuat PDF.";
      try {
        if (error.response?.data) msg = (await error.response.data.text()) || msg;
      } catch (e) {}
      swalError(msg);
    } finally {
      setDownloading("");
    }
  };

  // Alur kirim: hitung dulu (dryRun) -> konfirmasi dengan angka sebenarnya ->
  // kirim di latar belakang -> pantau progres sampai selesai.
  const sendDocuments = async (payload, label) => {
    try {
      const dry = await axios.post("/report/document/send", { ...payload, dryRun: true });
      const { willSend, alreadySent, newNumbers, skipped } = dry.data;
      const skippedHtml = listHtml("Dilewati", skipped, (r) => `<strong>${escapeHtml(r.name)}</strong> — ${escapeHtml(r.reason)}`);

      if (willSend === 0) {
        Swal.fire({
          title: "Tidak ada yang bisa dikirim",
          html: skippedHtml || "Tidak ada student yang cocok.",
          icon: "info",
          background: "white",
          color: "black",
          confirmButtonColor: TEAL,
        });
        return;
      }

      const confirm = await Swal.fire({
        title: `Kirim ${label}?`,
        html:
          `<p style="margin:0">Email berisi PDF akan dikirim ke <strong>${willSend} student</strong>.</p>` +
          (alreadySent > 0
            ? `<p style="margin:8px 0 0;font-size:0.9em;color:#b8860b"><strong>${alreadySent}</strong> di antaranya sudah pernah dikirimi dokumen ini dan akan menerimanya lagi (lihat tab Riwayat Kirim).</p>`
            : "") +
          (newNumbers > 0
            ? `<p style="margin:8px 0 0;font-size:0.9em">${newNumbers} nomor surat baru akan diterbitkan dan tidak bisa diubah lagi.</p>`
            : "") +
          skippedHtml,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Kirim",
        cancelButtonText: "Batal",
        background: "white",
        color: "black",
        confirmButtonColor: TEAL,
      });
      if (!confirm.isConfirmed) return;

      const started = await axios.post("/report/document/send", payload);
      const { jobId, total } = started.data;

      Swal.fire({
        title: "Mengirim...",
        html: `0 / ${total}`,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        background: "white",
        color: "black",
        didOpen: () => Swal.showLoading(),
      });

      let job = null;
      while (!job || job.status !== "finished") {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        job = (await axios.get(`/report/document/job/${jobId}`)).data;
        const box = Swal.getHtmlContainer();
        if (box) box.textContent = `${job.done} / ${job.total}`;
      }
      setHistTick((n) => n + 1);

      Swal.fire({
        title: job.failed.length === 0 ? "Selesai" : "Selesai dengan kegagalan",
        html:
          `<p style="margin:0">Terkirim: <strong>${job.sent.length}</strong> dari ${job.total}</p>` +
          listHtml("Gagal dikirim", job.failed, (r) => `<strong>${escapeHtml(r.name)}</strong> — ${escapeHtml(r.reason)}`) +
          listHtml("Dilewati", job.skipped, (r) => `<strong>${escapeHtml(r.name)}</strong> — ${escapeHtml(r.reason)}`),
        icon: job.failed.length === 0 ? "success" : "warning",
        background: "white",
        color: "black",
        confirmButtonColor: TEAL,
      });
    } catch (error) {
      swalError(error.response?.data?.msg || "Gagal mengirim dokumen.");
    }
  };

  // Hapus riwayat kirim: pilih satu hari atau rentang tanggal -> hitung dulu
  // (dryRun) -> konfirmasi dengan jumlah sebenarnya -> hapus.
  const deleteHistory = async () => {
    const today = todayIso();
    const picked = await Swal.fire({
      title: "Hapus riwayat kirim",
      html:
        `<div style="text-align:left;font-size:0.9em">` +
        `<label style="display:block;margin-bottom:6px"><input type="radio" name="hist-del-mode" value="day" checked> Satu hari</label>` +
        `<label style="display:block;margin-bottom:12px"><input type="radio" name="hist-del-mode" value="range"> Rentang tanggal</label>` +
        `<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">` +
        `<input type="date" id="hist-del-from" value="${today}" style="padding:6px 10px;border:1px solid #ddd;border-radius:6px">` +
        `<span id="hist-del-to-wrap" style="display:none">s/d <input type="date" id="hist-del-to" value="${today}" style="padding:6px 10px;border:1px solid #ddd;border-radius:6px"></span>` +
        `</div></div>`,
      showCancelButton: true,
      confirmButtonText: "Lanjut",
      cancelButtonText: "Batal",
      background: "white",
      color: "black",
      confirmButtonColor: "#b93030",
      didOpen: (popup) => {
        popup.querySelectorAll('input[name="hist-del-mode"]').forEach((radio) =>
          radio.addEventListener("change", () => {
            popup.querySelector("#hist-del-to-wrap").style.display = radio.value === "range" && radio.checked ? "inline" : "none";
          }),
        );
      },
      preConfirm: () => {
        const popup = Swal.getPopup();
        const mode = popup.querySelector('input[name="hist-del-mode"]:checked').value;
        const from = popup.querySelector("#hist-del-from").value;
        const to = mode === "range" ? popup.querySelector("#hist-del-to").value : from;
        if (!from || !to) return Swal.showValidationMessage("Tanggal wajib diisi");
        if (to < from) return Swal.showValidationMessage("Tanggal akhir tidak boleh sebelum tanggal awal");
        return { from, to };
      },
    });
    if (!picked.isConfirmed) return;

    // Batas hari memakai zona waktu browser: dari 00.00 tanggal awal sampai
    // 00.00 sehari SETELAH tanggal akhir (eksklusif).
    const { from, to } = picked.value;
    const [y, m, d] = to.split("-").map(Number);
    const body = { from: new Date(`${from}T00:00:00`).toISOString(), to: new Date(y, m - 1, d + 1).toISOString() };
    const dateText = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("id-ID", { dateStyle: "medium" });
    const rangeText = from === to ? `tanggal ${dateText(from)}` : `${dateText(from)} s/d ${dateText(to)}`;

    try {
      const dry = await axios.post("/report/document/history/delete", { ...body, dryRun: true });
      if (dry.data.count === 0) {
        Swal.fire({
          text: `Tidak ada riwayat kirim pada ${rangeText}.`,
          icon: "info",
          background: "white",
          color: "black",
          confirmButtonColor: TEAL,
        });
        return;
      }
      const confirm = await Swal.fire({
        title: `Hapus ${dry.data.count} riwayat?`,
        html:
          `<p style="margin:0">Semua riwayat kirim pada <strong>${rangeText}</strong> akan dihapus permanen.</p>` +
          `<p style="margin:8px 0 0;font-size:0.9em;color:#b8860b">Peringatan "sudah pernah dikirimi" untuk pengiriman ini tidak akan muncul lagi. Nomor surat SKL yang sudah terbit tidak ikut terhapus.</p>`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Hapus",
        cancelButtonText: "Batal",
        background: "white",
        color: "black",
        confirmButtonColor: "#b93030",
      });
      if (!confirm.isConfirmed) return;

      const res = await axios.post("/report/document/history/delete", body);
      setHistPage(1);
      setHistTick((n) => n + 1);
      Swal.fire({
        text: `${res.data.deleted} riwayat dihapus.`,
        icon: "success",
        background: "white",
        color: "black",
        confirmButtonColor: TEAL,
      });
    } catch (error) {
      swalError(error.response?.data?.msg || "Gagal menghapus riwayat.");
    }
  };

  const togglePicked = (setter) => (email) =>
    setter((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]));

  // Centang/lepas semua baris di halaman yang sedang tampil.
  const togglePage = (setter, pageEmails) =>
    setter((prev) =>
      pageEmails.every((e) => prev.includes(e))
        ? prev.filter((e) => !pageEmails.includes(e))
        : [...new Set([...prev, ...pageEmails])],
    );

  // Kelas dengan bobot tugas 0 (MMM) memang tidak punya tugas — kolom Nilai
  // Tugas disembunyikan supaya admin tidak mengira ada yang perlu diisi di
  // situ. Diturunkan dari bobot, bukan dari hardcode "MMM", supaya tetap benar
  // kalau bobot diubah lewat config REPORT_WEIGHTS.
  const noAssignment = Number(classReport?.weight?.assignment ?? 1) === 0;

  const weightLabel = useMemo(() => {
    const w = classReport?.weight;
    if (!w) return "";
    return `Bobot: kehadiran ${Math.round(w.attendance * 100)}% + tugas ${Math.round(w.assignment * 100)}%`;
  }, [classReport]);

  const Pager = ({ page, totalPages, onChange }) =>
    totalPages > 1 ? (
      <div className="d-flex align-items-center gap-2 mt-3 no-print">
        <button style={btn(false)} disabled={page <= 1} onClick={() => onChange(page - 1)}>
          Prev
        </button>
        <span style={{ fontSize: "0.85em", color: "#666" }}>
          Halaman {page} dari {totalPages}
        </span>
        <button style={btn(false)} disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
          Next
        </button>
      </div>
    ) : null;

  return (
    <div style={{ backgroundColor: "#F4F4F4", minHeight: "100vh" }}>
      {/* Saat di-print, hanya blok raport student yang dicetak — sidebar,
          tab, dan kontrol pencarian disembunyikan. */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-area { box-shadow: none !important; border: none !important; margin: 0 !important; }
          body { background: white !important; }
        }
      `}</style>

      {/* Offset sidebar & padding halaman diatur oleh .adm-main (admin-ui.css). */}
      <div className="adm-main">
        <div className="no-print">
          <PageHeader
            title="Report & Graduation"
            subtitle="Report cards per class and per student, graduation eligibility, and printable documents."
          />
        </div>

        <div className="d-flex gap-2 mb-4 flex-wrap no-print">
          <button style={btn(tab === "class")} onClick={() => setTab("class")}>
            Per Kelas
          </button>
          <button style={btn(tab === "student")} onClick={() => setTab("student")}>
            Raport Student
          </button>
          <button style={btn(tab === "graduation")} onClick={() => setTab("graduation")}>
            Graduation List
          </button>
          <button style={btn(tab === "history")} onClick={() => setTab("history")}>
            Riwayat Kirim
          </button>
        </div>

        {/* ══ TAB 1: PER KELAS ══════════════════════════════════════════════ */}
        {tab === "class" && (
          <div style={card}>
            <div className="d-flex gap-2 flex-wrap align-items-center mb-3">
              <select style={{ ...input, minWidth: "260px" }} value={selected} onChange={(e) => setSelected(e.target.value)}>
                {options.length === 0 && <option value="">Belum ada kelas yang punya sesi</option>}
                {options.map((o) => (
                  <option key={`${o.classCode}|${o.batch}`} value={`${o.classCode}|${o.batch}`}>
                    {o.classCode} — Batch {o.batch} ({o.sessionCount} sesi)
                  </option>
                ))}
              </select>
              <input
                style={{ ...input, minWidth: "220px" }}
                placeholder="Cari nama / email / student ID"
                value={classSearch}
                onChange={(e) => setClassSearch(e.target.value)}
              />
            </div>

            {classReport && (
              <p style={{ fontSize: "0.85em", color: "#666", marginBottom: "0.75rem" }}>
                <strong>{classReport.className}</strong> — Batch {classReport.batch} · {classReport.totalSessions} sesi ·{" "}
                {weightLabel} · {classReport.totalStudents} student
              </p>
            )}

            {classReport && classReport.students.length > 0 && (
              <div
                className="d-flex gap-2 flex-wrap align-items-center mb-3 p-2"
                style={{ backgroundColor: "#fafafa", borderRadius: "6px" }}
              >
                <span style={{ fontSize: "0.85em", color: "#555", fontWeight: 600 }}>
                  <i className="bi bi-envelope me-1"></i> Kirim Attendance Summary (PDF):
                </span>
                <button
                  style={{ ...btn(classPicked.length > 0), padding: "6px 12px", fontSize: "0.85em" }}
                  disabled={classPicked.length === 0}
                  onClick={() =>
                    sendDocuments(
                      { type: "monthly", classCode: classReport.classCode, batch: classReport.batch, emails: classPicked },
                      `${DOC_LABEL.monthly} ${classReport.classCode} Batch ${classReport.batch}`,
                    )
                  }
                >
                  Kirim ke terpilih ({classPicked.length})
                </button>
                <button
                  style={{ ...btn(true), padding: "6px 12px", fontSize: "0.85em" }}
                  onClick={() =>
                    sendDocuments(
                      { type: "monthly", classCode: classReport.classCode, batch: classReport.batch, all: true },
                      `${DOC_LABEL.monthly} ${classReport.classCode} Batch ${classReport.batch}`,
                    )
                  }
                >
                  Kirim ke semua student kelas ini ({classReport.totalStudents})
                </button>
              </div>
            )}

            {loadingClass ? (
              <p style={{ color: "#888" }}>Loading...</p>
            ) : !classReport || classReport.students.length === 0 ? (
              <p style={{ color: "#888" }}>Tidak ada data untuk kelas/batch ini.</p>
            ) : (
              <div className="attendance-sticky-table">
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "800px" }}>
                  <thead>
                    <tr>
                      <th style={th}>
                        <input
                          type="checkbox"
                          title="Pilih semua di halaman ini"
                          checked={classReport.students.every((s) => classPicked.includes(s.email))}
                          onChange={() =>
                            togglePage(
                              setClassPicked,
                              classReport.students.map((s) => s.email),
                            )
                          }
                        />
                      </th>
                      <th style={th}>Student</th>
                      <th style={th}>Tipe</th>
                      <th style={th}>JS</th>
                      <th style={th}>TH</th>
                      <th style={th}>PT%</th>
                      <th style={th}>Att. Score</th>
                      {!noAssignment && <th style={th}>Nilai Tugas</th>}
                      <th style={th}>Total</th>
                      <th style={th}>Grade</th>
                      <th style={th}>Status</th>
                      <th style={th}>PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classReport.students.map((s) => (
                      <tr key={s.email}>
                        <td style={td}>
                          <input
                            type="checkbox"
                            checked={classPicked.includes(s.email)}
                            onChange={() => togglePicked(setClassPicked)(s.email)}
                          />
                        </td>
                        <td style={td}>
                          <span
                            onClick={() => openStudent(s.email)}
                            style={{ color: TEAL, cursor: "pointer", fontWeight: 600 }}
                          >
                            {s.name}
                          </span>
                          <div style={{ fontSize: "0.85em", color: "#999" }}>{s.studentId || s.email}</div>
                        </td>
                        <td style={td}>{s.classType}</td>
                        <td style={td}>{s.js}</td>
                        <td style={td}>{s.th}</td>
                        <td style={td}>{s.pt}%</td>
                        <td style={td}>{s.attendanceScore}</td>
                        {!noAssignment && (
                        <td style={td}>
                          <div className="d-flex align-items-center gap-1">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              className="grading-score-input"
                              style={{
                                width: "72px",
                                padding: "4px 6px",
                                borderRadius: "5px",
                                // Kuning = belum ada nilai dari sumber mana pun,
                                // jadi total-nya masih dihitung dengan tugas = 0.
                                border: s.needsAssignmentInput ? "1px solid #e0b84c" : "1px solid #ddd",
                                backgroundColor: s.needsAssignmentInput ? "#fffaf0" : "white",
                                fontSize: "0.92em",
                              }}
                              disabled={!s.taken || !!savingScore[s.email]}
                              value={
                                scoreDraft[s.email] !== undefined
                                  ? scoreDraft[s.email]
                                  : s.assignmentScore === null || s.assignmentScore === undefined
                                    ? ""
                                    : s.assignmentScore
                              }
                              onChange={(e) => setScoreDraft((p) => ({ ...p, [s.email]: e.target.value }))}
                              onBlur={() => saveScore(s)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") e.currentTarget.blur();
                              }}
                            />
                            {savingScore[s.email] ? (
                              <span style={{ fontSize: "0.72em", color: "#999" }}>...</span>
                            ) : s.assignmentSource === "manual" ? (
                              <span
                                title={
                                  (s.manualNote ? s.manualNote + " — " : "") +
                                  "diisi " +
                                  (s.manualEnteredBy || "admin") +
                                  ". Kosongkan sel untuk kembali ke nilai Google Classroom."
                                }
                                style={{
                                  backgroundColor: "#e6f4ee",
                                  color: "#1d7a52",
                                  borderRadius: "4px",
                                  padding: "1px 5px",
                                  fontSize: "0.68em",
                                  fontWeight: 700,
                                  cursor: "help",
                                }}
                              >
                                MANUAL
                              </span>
                            ) : s.assignmentSource === "classroom" ? (
                              <span
                                title={`Rata-rata ${s.assignmentCount} tugas dari Google Classroom. Ketik angka untuk menimpanya.`}
                                style={{ fontSize: "0.72em", color: "#999", cursor: "help" }}
                              >
                                GC {s.assignmentCount}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        )}
                        <td style={{ ...td, fontWeight: 700 }}>{num(s.total)}</td>
                        <td style={td}>
                          <Grade letter={s.grade} />
                        </td>
                        <td style={td}>
                          <Badge status={s.status} />
                        </td>
                        <td style={td}>
                          <button
                            style={smallBtn}
                            title="Unduh attendance summary student ini untuk diperiksa"
                            disabled={downloading === `monthly|${s.email}`}
                            onClick={() =>
                              downloadDocument(
                                { type: "monthly", email: s.email, classCode: classReport.classCode, batch: classReport.batch },
                                `monthly|${s.email}`,
                              )
                            }
                          >
                            {downloading === `monthly|${s.email}` ? "..." : <i className="bi bi-download"></i>}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {classReport && <Pager page={classReport.page} totalPages={classReport.totalPages} onChange={setClassPage} />}
          </div>
        )}

        {/* ══ TAB 2: RAPORT STUDENT ═════════════════════════════════════════ */}
        {tab === "student" && (
          <>
            <div style={card} className="no-print">
              <input
                style={{ ...input, width: "100%", maxWidth: "420px" }}
                placeholder="Cari student: nama / email / student ID"
                value={studentQuery}
                onChange={(e) => setStudentQuery(e.target.value)}
              />
              {candidates.length > 0 && (
                <div className="mt-2">
                  {candidates.map((c) => (
                    <div
                      key={c.email}
                      onClick={() => openStudent(c.email)}
                      style={{
                        padding: "8px 10px",
                        borderBottom: "1px solid #f2f2f2",
                        cursor: "pointer",
                        fontSize: "0.9em",
                      }}
                    >
                      <strong>{c.name}</strong>{" "}
                      <span style={{ color: "#999" }}>
                        {c.studentId || ""} · {c.email}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {loadingStudent && <p style={{ color: "#888" }}>Loading...</p>}

            {studentReport && (
              <div style={card} className="print-area" ref={printRef}>
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                  <div>
                    <p style={{ fontSize: "1.2em", fontWeight: 700, marginBottom: "0.2rem" }}>
                      {studentReport.student.name}
                    </p>
                    <p style={{ fontSize: "0.85em", color: "#666", marginBottom: 0 }}>
                      {studentReport.student.studentId} · {studentReport.student.email}
                      <br />
                      {studentReport.student.program} · {studentReport.student.classType} ·{" "}
                      {studentReport.student.localChurch}
                    </p>
                  </div>
                </div>

                <div
                  className="d-flex flex-wrap gap-3 mt-3 mb-3 p-3"
                  style={{ backgroundColor: "#fafafa", borderRadius: "6px" }}
                >
                  {[
                    ["Kelas diambil", studentReport.summary.classesTaken],
                    ["Kelas lulus", studentReport.summary.countLulus],
                    ["Total score", studentReport.summary.totalScore],
                    ["Nilai rapot akhir", studentReport.summary.finalGrade],
                    [
                      "Rincian kehadiran",
                      `${studentReport.summary.attendanceOkCount} / ${studentReport.summary.totalClasses}`,
                    ],
                    [
                      "Rincian nilai tugas",
                      `${studentReport.summary.assignmentOkCount} / ${studentReport.summary.totalClasses}`,
                    ],
                  ].map(([label, value]) => (
                    <div key={label} style={{ minWidth: "130px" }}>
                      <p style={{ fontSize: "0.75em", color: "#888", marginBottom: 0 }}>{label}</p>
                      <p style={{ fontSize: "1.05em", fontWeight: 700, marginBottom: 0 }}>{value}</p>
                    </div>
                  ))}
                  <div style={{ minWidth: "150px" }}>
                    <p style={{ fontSize: "0.75em", color: "#888", marginBottom: 0 }}>Status kelulusan</p>
                    <p
                      style={{
                        fontSize: "1.05em",
                        fontWeight: 700,
                        marginBottom: 0,
                        color: studentReport.summary.eligible ? "#1d7a52" : "#b93030",
                      }}
                    >
                      {studentReport.summary.eligible ? "ELIGIBLE GRADUATE" : "BELUM ELIGIBLE"}
                    </p>
                  </div>
                </div>

                {/* Dokumen PDF untuk student ini: unduh dulu untuk diperiksa,
                    lalu kirim ke emailnya. */}
                <div
                  className="no-print mb-3 p-3"
                  style={{ border: "1px solid #eee", borderRadius: "6px", fontSize: "0.88em" }}
                >
                  <p style={{ fontWeight: 600, marginBottom: "0.5rem" }}>
                    <i className="bi bi-envelope me-1"></i> Dokumen untuk student
                  </p>
                  {["semester1", "semester2", "skl"].map((docKey) => {
                    const key = `${docKey}|${studentReport.student.email}`;
                    return (
                      <div key={docKey} className="d-flex align-items-center gap-2 flex-wrap mb-2">
                        <span style={{ minWidth: "240px" }}>{DOC_LABEL[docKey]}</span>
                        {docKey === "skl" && (
                          <input
                            type="date"
                            style={{ ...input, padding: "3px 8px", fontSize: "0.9em" }}
                            title="Tanggal dokumen — hanya dipakai kalau nomor surat student ini belum pernah diterbitkan"
                            value={sklDate}
                            onChange={(e) => setSklDate(e.target.value)}
                          />
                        )}
                        <button
                          style={smallBtn}
                          disabled={downloading === key}
                          onClick={() =>
                            downloadDocument({ ...docParams(docKey, sklDate), email: studentReport.student.email }, key)
                          }
                        >
                          {downloading === key ? "..." : "Unduh PDF"}
                        </button>
                        <button
                          style={{ ...smallBtn, backgroundColor: TEAL, color: "white" }}
                          onClick={() =>
                            sendDocuments(
                              { ...docParams(docKey, sklDate), emails: [studentReport.student.email] },
                              `${DOC_LABEL[docKey]} ke ${studentReport.student.name}`,
                            )
                          }
                        >
                          Kirim email
                        </button>
                      </div>
                    );
                  })}
                  <p style={{ fontSize: "0.85em", color: "#999", margin: 0 }}>
                    Transkrip Nilai hanya bisa dibuat untuk student yang eligible graduate. Nomor suratnya diterbitkan
                    saat pertama kali dikirim dan dipakai lagi setiap kali dikirim ulang.
                  </p>
                </div>

                {studentReport.summary.reasons.length > 0 && (
                  <div
                    style={{
                      backgroundColor: "#fff8e6",
                      border: "1px solid #f2e2b8",
                      borderRadius: "6px",
                      padding: "10px 12px",
                      marginBottom: "1rem",
                      fontSize: "0.85em",
                    }}
                  >
                    <strong>Belum memenuhi syarat karena:</strong>
                    <ul style={{ marginBottom: 0, paddingLeft: "1.2rem" }}>
                      {studentReport.summary.reasons.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="attendance-sticky-table">
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "900px" }}>
                    <thead>
                      <tr>
                        <th style={th}>Kelas</th>
                        <th style={th}>Batch</th>
                        <th style={th}>JS / Sesi</th>
                        <th style={th}>PT%</th>
                        <th style={th}>Att. Score</th>
                        <th style={th}>Att. AG</th>
                        <th style={th}>Nilai Tugas</th>
                        <th style={th}>Total</th>
                        <th style={th}>Grade</th>
                        <th style={th}>Status</th>
                        <th style={th} className="no-print">
                          Summary
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {studentReport.classes.map((c) => (
                        <tr key={c.classCode} style={{ opacity: c.taken ? 1 : 0.55 }}>
                          <td style={td}>
                            <strong>{c.classCode}</strong>
                            <div style={{ fontSize: "0.85em", color: "#999" }}>{c.className}</div>
                          </td>
                          <td style={td}>{num(c.batch)}</td>
                          <td style={td}>
                            {c.taken ? `${c.js} / ${c.totalSessions}` : "—"}
                          </td>
                          <td style={td}>{c.taken ? `${c.pt}%` : "—"}</td>
                          <td style={td}>{c.taken ? c.attendanceScore : "—"}</td>
                          <td style={td}>{c.taken ? <Grade letter={c.attendanceLetter} /> : "—"}</td>
                          <td style={td}>
                            {c.taken ? (
                              <>
                                {num(c.assignmentScore)}
                                {c.assignmentCount > 0 && (
                                  <span style={{ fontSize: "0.8em", color: "#999" }}> ({c.assignmentCount})</span>
                                )}
                              </>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td style={{ ...td, fontWeight: 700 }}>{num(c.total)}</td>
                          <td style={td}>
                            <Grade letter={c.grade} />
                          </td>
                          <td style={td}>
                            <Badge status={c.status} />
                            {c.notTakenReason === "no_sessions" && (
                              <div style={{ fontSize: "0.75em", color: "#999" }}>sesi belum dibuat</div>
                            )}
                          </td>
                          {/* Attendance summary kelas ini untuk student ini.
                              Hanya ada untuk kelas yang sudah diambil — kelas
                              lain belum punya sesi untuk dirangkum. */}
                          <td style={td} className="no-print">
                            {c.taken ? (
                              <div className="d-flex gap-1">
                                <button
                                  style={smallBtn}
                                  title={`Unduh attendance summary ${c.classCode} untuk diperiksa`}
                                  disabled={downloading === `monthly|${c.classCode}|${studentReport.student.email}`}
                                  onClick={() =>
                                    downloadDocument(
                                      {
                                        type: "monthly",
                                        email: studentReport.student.email,
                                        classCode: c.classCode,
                                        batch: c.batch,
                                      },
                                      `monthly|${c.classCode}|${studentReport.student.email}`,
                                    )
                                  }
                                >
                                  {downloading === `monthly|${c.classCode}|${studentReport.student.email}` ? (
                                    "..."
                                  ) : (
                                    <i className="bi bi-download"></i>
                                  )}
                                </button>
                                <button
                                  style={{ ...smallBtn, backgroundColor: TEAL, color: "white" }}
                                  title={`Kirim attendance summary ${c.classCode} ke email student`}
                                  onClick={() =>
                                    sendDocuments(
                                      {
                                        type: "monthly",
                                        classCode: c.classCode,
                                        batch: c.batch,
                                        emails: [studentReport.student.email],
                                      },
                                      `${DOC_LABEL.monthly} ${c.classCode} Batch ${c.batch} ke ${studentReport.student.name}`,
                                    )
                                  }
                                >
                                  <i className="bi bi-send me-1"></i>Kirim
                                </button>
                              </div>
                            ) : (
                              <span style={{ color: "#bbb" }}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p style={{ fontSize: "0.75em", color: "#999", marginTop: "0.75rem", marginBottom: 0 }}>
                  Syarat graduate: kehadiran memenuhi (grade A/B/C) di {studentReport.rule.minAttendanceOk} kelas DAN
                  nilai tugas memenuhi di minimal {studentReport.rule.minAssignmentOk} kelas. Kelas MMM selalu
                  terhitung memenuhi untuk nilai tugas karena tidak punya tugas.
                </p>
              </div>
            )}

            {!loadingStudent && !studentReport && (
              <p style={{ color: "#888" }}>Cari student di atas untuk melihat raportnya.</p>
            )}
          </>
        )}

        {/* ══ TAB 3: GRADUATION LIST ════════════════════════════════════════ */}
        {tab === "graduation" && (
          <div style={card}>
            <div className="d-flex gap-2 flex-wrap mb-3">
              <input
                style={{ ...input, minWidth: "240px" }}
                placeholder="Cari nama / email / student ID"
                value={gradSearch}
                onChange={(e) => setGradSearch(e.target.value)}
              />
              <input
                style={{ ...input, width: "140px" }}
                placeholder="Batch joined"
                value={gradBatch}
                onChange={(e) => {
                  setGradBatch(e.target.value);
                  setGradPage(1);
                }}
              />
              <select
                style={input}
                value={gradStatus}
                onChange={(e) => {
                  setGradStatus(e.target.value);
                  setGradPage(1);
                }}
              >
                <option value="">Semua status</option>
                <option value="eligible">Eligible</option>
                <option value="not_eligible">Not Eligible</option>
              </select>
            </div>

            {/* Angka ini dihitung atas SELURUH student yang cocok filter, bukan
                cuma halaman yang terbuka — karena itu endpoint-nya terpisah. */}
            <div
              className="d-flex flex-wrap gap-3 mb-3 p-3"
              style={{ backgroundColor: "#fafafa", borderRadius: "6px" }}
            >
              {gradStats === null ? (
                <p style={{ margin: 0, fontSize: "0.85em", color: "#888" }}>Menghitung...</p>
              ) : (
                <>
                  <div style={{ minWidth: "110px" }}>
                    <p style={{ fontSize: "0.75em", color: "#888", marginBottom: 0 }}>Eligible</p>
                    <p style={{ fontSize: "1.4em", fontWeight: 700, marginBottom: 0, color: STATUS_STYLE.PASSED.fg }}>
                      {gradStats.eligible}
                    </p>
                  </div>
                  <div style={{ minWidth: "110px" }}>
                    <p style={{ fontSize: "0.75em", color: "#888", marginBottom: 0 }}>Not Eligible</p>
                    <p style={{ fontSize: "1.4em", fontWeight: 700, marginBottom: 0, color: STATUS_STYLE.FAIL.fg }}>
                      {gradStats.notEligible}
                    </p>
                  </div>
                  <div style={{ minWidth: "110px" }}>
                    <p style={{ fontSize: "0.75em", color: "#888", marginBottom: 0 }}>Total student</p>
                    <p style={{ fontSize: "1.4em", fontWeight: 700, marginBottom: 0 }}>{gradStats.total}</p>
                  </div>
                  <div style={{ flex: 1, minWidth: "180px", alignSelf: "center" }}>
                    <p style={{ fontSize: "0.78em", color: "#888", marginBottom: 0 }}>
                      {gradBatch || appliedGradSearch ? (
                        <>
                          Mengikuti filter yang aktif
                          {gradBatch ? ` · batch joined ${gradBatch}` : ""}
                          {appliedGradSearch ? ` · pencarian "${appliedGradSearch}"` : ""}
                        </>
                      ) : (
                        "Seluruh student (tanpa filter)"
                      )}
                    </p>
                  </div>
                </>
              )}
            </div>

            <div
              className="d-flex gap-2 flex-wrap align-items-center mb-3 p-2"
              style={{ backgroundColor: "#fafafa", borderRadius: "6px" }}
            >
              <span style={{ fontSize: "0.85em", color: "#555", fontWeight: 600 }}>
                <i className="bi bi-envelope me-1"></i> Kirim dokumen (PDF):
              </span>
              <select style={{ ...input, fontSize: "0.85em" }} value={gradDoc} onChange={(e) => setGradDoc(e.target.value)}>
                <option value="semester1">{DOC_LABEL.semester1}</option>
                <option value="semester2">{DOC_LABEL.semester2}</option>
                <option value="skl">{DOC_LABEL.skl}</option>
              </select>
              {gradDoc === "skl" && (
                <input
                  type="date"
                  style={{ ...input, fontSize: "0.85em" }}
                  title="Tanggal dokumen — menentukan bulan & tahun di nomor surat yang baru diterbitkan"
                  value={sklDate}
                  onChange={(e) => setSklDate(e.target.value)}
                />
              )}
              <button
                style={{ ...btn(gradPicked.length > 0), padding: "6px 12px", fontSize: "0.85em" }}
                disabled={gradPicked.length === 0}
                onClick={() => sendDocuments({ ...docParams(gradDoc, sklDate), emails: gradPicked }, DOC_LABEL[gradDoc])}
              >
                Kirim ke terpilih ({gradPicked.length})
              </button>
              <button
                style={{ ...btn(true), padding: "6px 12px", fontSize: "0.85em" }}
                title="Mengikuti pencarian, batch joined, dan status yang sedang aktif"
                onClick={() =>
                  sendDocuments(
                    {
                      ...docParams(gradDoc, sklDate),
                      all: true,
                      filters: { search: appliedGradSearch, batchJoined: gradBatch, status: gradStatus },
                    },
                    DOC_LABEL[gradDoc],
                  )
                }
              >
                Kirim ke semua sesuai filter
              </button>
              {gradPicked.length > 0 && (
                <span
                  onClick={() => setGradPicked([])}
                  style={{ fontSize: "0.8em", color: TEAL, cursor: "pointer", textDecoration: "underline" }}
                >
                  kosongkan pilihan
                </span>
              )}
            </div>

            {/* Filter eligible dihitung SETELAH paginasi (bukan kolom DB), jadi
                jumlah baris per halaman bisa lebih sedikit dari 25. Dijelaskan
                di layar supaya tidak disalahartikan sebagai data hilang. */}
            {gradList?.filtered && (
              <p style={{ fontSize: "0.8em", color: "#888" }}>
                Filter status diterapkan per halaman — halaman ini menampilkan {gradList.students.length} dari{" "}
                {gradList.pageSize} student yang dimuat.
              </p>
            )}

            {loadingGrad ? (
              <p style={{ color: "#888" }}>Loading...</p>
            ) : !gradList || gradList.students.length === 0 ? (
              <p style={{ color: "#888" }}>Tidak ada student yang cocok.</p>
            ) : (
              <div className="attendance-sticky-table">
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "900px" }}>
                  <thead>
                    <tr>
                      <th style={th}>
                        <input
                          type="checkbox"
                          title="Pilih semua di halaman ini"
                          checked={gradList.students.every((s) => gradPicked.includes(s.email))}
                          onChange={() =>
                            togglePage(
                              setGradPicked,
                              gradList.students.map((s) => s.email),
                            )
                          }
                        />
                      </th>
                      <th style={th}>Student</th>
                      <th style={th}>Program</th>
                      <th style={th}>Kelas diambil</th>
                      <th style={th}>Lulus</th>
                      <th style={th}>Kehadiran OK</th>
                      <th style={th}>Tugas OK</th>
                      <th style={th}>Total score</th>
                      <th style={th}>Rapot</th>
                      <th style={th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gradList.students.map((s) => (
                      <tr key={s.email}>
                        <td style={td}>
                          <input
                            type="checkbox"
                            checked={gradPicked.includes(s.email)}
                            onChange={() => togglePicked(setGradPicked)(s.email)}
                          />
                        </td>
                        <td style={td}>
                          <span
                            onClick={() => openStudent(s.email)}
                            style={{ color: TEAL, cursor: "pointer", fontWeight: 600 }}
                          >
                            {s.name}
                          </span>
                          <div style={{ fontSize: "0.85em", color: "#999" }}>{s.studentId || s.email}</div>
                        </td>
                        <td style={td}>{s.program}</td>
                        <td style={td}>{s.classesTaken}</td>
                        <td style={td}>{s.countLulus}</td>
                        {/* Penyebutnya jumlah kelas yang dinilai (10), bukan
                            ambang kelulusan — progres atas seluruh kelas lebih
                            mudah dibaca, dan ambangnya berbeda antar-angkatan.
                            Ambangnya disebut di tooltip. */}
                        <td style={td} title={`Syarat lulus: minimal ${gradList.rule.minAttendanceOk}`}>
                          {s.attendanceOkCount} / {s.totalClasses}
                        </td>
                        <td
                          style={td}
                          title={`Syarat lulus: minimal ${gradList.rule.minAssignmentOk}. Kelas MMM selalu terhitung memenuhi karena tidak punya tugas.`}
                        >
                          {s.assignmentOkCount} / {s.totalClasses}
                        </td>
                        <td style={td}>{s.totalScore}</td>
                        <td style={td}>
                          <Grade letter={s.finalGrade} />
                        </td>
                        <td style={td}>
                          <span
                            style={{
                              backgroundColor: s.eligible ? STATUS_STYLE.PASSED.bg : STATUS_STYLE.FAIL.bg,
                              color: s.eligible ? STATUS_STYLE.PASSED.fg : STATUS_STYLE.FAIL.fg,
                              borderRadius: "4px",
                              padding: "2px 8px",
                              fontSize: "0.78em",
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {s.eligible ? "Eligible" : "Not Eligible"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {gradList && <Pager page={gradList.page} totalPages={gradList.totalPages} onChange={setGradPage} />}
          </div>
        )}

        {/* ══ TAB 4: RIWAYAT KIRIM ══════════════════════════════════════════ */}
        {tab === "history" && (
          <div style={card}>
            <div className="d-flex gap-2 flex-wrap mb-3">
              <input
                style={{ ...input, minWidth: "260px" }}
                placeholder="Cari nama / email / nomor surat"
                value={histSearch}
                onChange={(e) => setHistSearch(e.target.value)}
              />
              <select
                style={input}
                value={histType}
                onChange={(e) => {
                  setHistType(e.target.value);
                  setHistPage(1);
                }}
              >
                <option value="">Semua dokumen</option>
                <option value="monthly">Attendance Summary</option>
                <option value="semester">Attendance &amp; Grading Semester</option>
                <option value="skl">Transkrip Nilai (SKL)</option>
              </select>
              <select
                style={input}
                value={histStatus}
                onChange={(e) => {
                  setHistStatus(e.target.value);
                  setHistPage(1);
                }}
              >
                <option value="">Semua status</option>
                <option value="sent">Terkirim</option>
                <option value="failed">Gagal</option>
              </select>
              <button
                style={{ ...smallBtn, marginLeft: "auto", padding: "6px 12px", fontSize: "0.85em", borderColor: "#b93030", color: "#b93030" }}
                onClick={deleteHistory}
              >
                <i className="bi bi-trash me-1"></i>Hapus Riwayat
              </button>
            </div>

            {history && (
              <p style={{ fontSize: "0.85em", color: "#666", marginBottom: "0.75rem" }}>{history.total} pengiriman tercatat</p>
            )}

            {loadingHist ? (
              <p style={{ color: "#888" }}>Loading...</p>
            ) : !history || history.rows.length === 0 ? (
              <p style={{ color: "#888" }}>Belum ada pengiriman yang cocok.</p>
            ) : (
              <div className="attendance-sticky-table">
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "860px" }}>
                  <thead>
                    <tr>
                      <th style={th}>Waktu</th>
                      <th style={th}>Student</th>
                      <th style={th}>Dokumen</th>
                      <th style={th}>Nomor surat</th>
                      <th style={th}>Status</th>
                      <th style={th}>Dikirim oleh</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.rows.map((r) => (
                      <tr key={r.id}>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>
                          {new Date(r.sentAt).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}
                        </td>
                        <td style={td}>
                          <span onClick={() => openStudent(r.studentEmail)} style={{ color: TEAL, cursor: "pointer", fontWeight: 600 }}>
                            {r.studentName || r.studentEmail}
                          </span>
                          <div style={{ fontSize: "0.85em", color: "#999" }}>{r.studentEmail}</div>
                        </td>
                        <td style={td}>
                          {r.type === "monthly"
                            ? `${DOC_LABEL.monthly} — ${r.classCode} Batch ${r.batch}`
                            : r.type === "semester"
                              ? DOC_LABEL[`semester${r.semester}`]
                              : DOC_LABEL.skl}
                        </td>
                        <td style={td}>{num(r.documentNumber)}</td>
                        <td style={td}>
                          <span
                            title={r.error || ""}
                            style={{
                              backgroundColor: r.status === "sent" ? STATUS_STYLE.PASSED.bg : STATUS_STYLE.FAIL.bg,
                              color: r.status === "sent" ? STATUS_STYLE.PASSED.fg : STATUS_STYLE.FAIL.fg,
                              borderRadius: "4px",
                              padding: "2px 8px",
                              fontSize: "0.78em",
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {r.status === "sent" ? "Terkirim" : "Gagal"}
                          </span>
                          {r.status !== "sent" && r.error && (
                            <div style={{ fontSize: "0.78em", color: "#b93030", maxWidth: "260px" }}>{r.error}</div>
                          )}
                        </td>
                        <td style={td}>{num(r.sentBy)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {history && <Pager page={history.page} totalPages={history.totalPages} onChange={setHistPage} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportPage;
